/**
 * API origin for the Node backend.
 *
 * - CRA embeds env at build/dev-server start.
 * - Set REACT_APP_FORCE_RELATIVE_API=1 to always use relative `/api/...` (dev proxy).
 */
export function getApiBaseUrl() {
  if (String(process.env.REACT_APP_FORCE_RELATIVE_API || "").trim() === "1") {
    return "";
  }
  const raw = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_GATEWAY_URL || "";
  const trimmed = String(raw).trim().replace(/\/$/, "");
  if (trimmed) return trimmed;
  return "";
}

export const API_BASE_URL = getApiBaseUrl();

export function apiUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  const base = API_BASE_URL.replace(/\/$/, "");
  if (!base) return p;
  return `${base}${p}`;
}

export async function fetchJson(url, options = {}, timeoutMs = 20000) {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), timeoutMs);
  const headers = { ...(options.headers || {}) };
  if (options.body != null && headers["Content-Type"] == null && headers["content-type"] == null) {
    headers["Content-Type"] = "application/json";
  }
  try {
    const res = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers,
    });
    const text = await res.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = { error: text ? text.slice(0, 400) : "Invalid JSON from server" };
    }
    if (!res.ok) {
      const msg =
        (data && typeof data.error === "string" && data.error) ||
        (data && typeof data.message === "string" && data.message) ||
        `Request failed (${res.status})`;
      const err = new Error(msg);
      err.status = res.status;
      err.body = data;
      throw err;
    }
    return data;
  } catch (e) {
    if (e && e.name === "AbortError") {
      throw new Error(
        `Request timed out after ${timeoutMs / 1000}s — is the backend running and reachable?`
      );
    }
    const msg = e && typeof e.message === "string" ? e.message : "";
    const name = e && typeof e.name === "string" ? e.name : "";
    const looksNetwork =
      msg === "Failed to fetch" ||
      msg === "Load failed" ||
      msg.includes("NetworkError") ||
      (name === "TypeError" && /fetch|network|load/i.test(msg));
    if (looksNetwork) {
      const base = getApiBaseUrl();
      const hint = base
        ? `Could not reach ${base}. Check the URL, HTTPS/certificate, and CORS.`
        : "Could not reach the API. Start the backend (cd backend && npm start, default port 5001), restart npm start in frontend, or set REACT_APP_API_BASE_URL if the API is not proxied.";
      throw new Error(hint);
    }
    throw e;
  } finally {
    clearTimeout(tid);
  }
}

export function apiGet(path, options = {}) {
  return fetchJson(apiUrl(path), options);
}

export function apiPost(path, body, options = {}) {
  return fetchJson(apiUrl(path), {
    ...options,
    method: "POST",
    body: JSON.stringify(body),
  });
}
